import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { OrdenVisitaDTO } from '@ausentia/shared';
import { api, ErrorApi } from '../api/cliente.js';
import { useSesion } from '../contexto/Sesion.js';
import { useRed } from '../componentes/useRed.js';
import { guardarOrdenes, ordenesCacheadas } from '../almacen/db.js';
import { iniciarBorrador } from './flujo.js';

const INSIGNIA: Record<string, { txt: string; clase: string }> = {
  FIRMADA: { txt: 'Firmada', clase: 'verde' },
  FIRMA_PENDIENTE: { txt: 'Por sincronizar', clase: 'ambar' },
  NO_REALIZADA: { txt: 'No realizada', clase: 'gris' },
  PENDIENTE: { txt: 'Pendiente', clase: 'gris' },
  EN_CURSO: { txt: 'En curso', clase: 'azul' },
};

export function MisVisitas() {
  const { medico } = useSesion();
  const online = useRed();
  const navegar = useNavigate();
  const [ordenes, setOrdenes] = useState<OrdenVisitaDTO[] | null>(null);
  const [error, setError] = useState('');
  const [busqueda, setBusqueda] = useState('');

  const totalVisitas = ordenes ? ordenes.length : 0;
  const completadas = useMemo(() => {
    return ordenes
      ? ordenes.filter(
          (o) =>
            o.estadoVisita === 'FIRMADA' ||
            o.estadoVisita === 'NO_REALIZADA' ||
            o.estadoVisita === 'FIRMA_PENDIENTE',
        ).length
      : 0;
  }, [ordenes]);
  const porcentaje = useMemo(() => {
    return totalVisitas > 0 ? Math.round((completadas / totalVisitas) * 100) : 0;
  }, [totalVisitas, completadas]);

  const ordenesFiltradas = useMemo(() => {
    if (!ordenes) return [];
    const busq = busqueda.toLowerCase();
    return ordenes.filter(
      (o) =>
        o.trabajador.nombreCompleto.toLowerCase().includes(busq) ||
        o.empresa.nombre.toLowerCase().includes(busq) ||
        o.domicilio.toLowerCase().includes(busq),
    );
  }, [ordenes, busqueda]);

  // Ruta con todos los controles pendientes del día, en el orden de la agenda.
  // Sin API key: Google Maps geocodea las direcciones al abrir el link.
  const urlRutaDia = useMemo(() => {
    if (!ordenes) return null;
    const pendientes = ordenes
      .filter((o) => o.estadoVisita === 'PENDIENTE')
      .sort((a, b) => a.programadaPara.localeCompare(b.programadaPara))
      .map((o) => o.domicilio);
    if (pendientes.length === 0) return null;
    const destino = pendientes[pendientes.length - 1];
    const escalas = pendientes.slice(0, -1);
    const params = new URLSearchParams({ api: '1', destination: destino, travelmode: 'driving' });
    if (escalas.length > 0) params.set('waypoints', escalas.join('|'));
    return `https://www.google.com/maps/dir/?${params.toString()}`;
  }, [ordenes]);

  useEffect(() => {
    let activo = true;
    (async () => {
      try {
        if (online) {
          const r = await api.get<{ ordenes: OrdenVisitaDTO[] }>('/api/ordenes');
          await guardarOrdenes(r.ordenes);
          if (activo) setOrdenes(r.ordenes);
        } else {
          if (activo) setOrdenes(await ordenesCacheadas());
        }
      } catch (err) {
        if (activo) {
          setError(err instanceof ErrorApi ? err.message : 'No se pudieron cargar las visitas');
          setOrdenes(await ordenesCacheadas());
        }
      }
    })();
    return () => {
      activo = false;
    };
  }, [online]);

  function iniciar(orden: OrdenVisitaDTO) {
    iniciarBorrador({ orden, resultado: null });
    navegar('/medico/control');
  }

  const hoy = new Date().toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="contenido">
      <div className="fila" style={{ paddingTop: 6 }}>
        <div>
          <p style={{ margin: 0, fontWeight: 600 }}>Ausentia</p>
          <p className="muted" style={{ margin: '2px 0 0' }}>
            {medico?.nombre} · {medico?.matricula.tipo} {medico?.matricula.numero} ({medico?.matricula.jurisdiccion})
          </p>
        </div>
        {medico?.refeps.validada ? (
          <span className="insignia verde">🛡️ REFEPS</span>
        ) : (
          <span className="insignia roja">REFEPS pendiente</span>
        )}
      </div>

      <div className="fila" style={{ margin: '10px 0' }}>
        <span className="muted" style={{ textTransform: 'capitalize' }}>{hoy}</span>
        <span className="mini" style={{ color: online ? 'var(--verde)' : 'var(--ambar-texto)' }}>
          ● {online ? 'En línea' : 'Sin conexión'}
        </span>
      </div>

      {error && <div className="error-box">{error}</div>}
      {ordenes === null && <div className="cargando">Cargando visitas…</div>}
      {ordenes && (
        <>
          {/* Barra de progreso de visitas diarias */}
          <div style={{ background: 'var(--panel-2)', borderRadius: 'var(--r-md)', padding: '10px 12px', marginBottom: 12 }}>
            <div className="fila" style={{ fontSize: 11.5, fontWeight: 600 }}>
              <span className="muted">Progreso diario</span>
              <span style={{ color: 'var(--verde-texto)' }}>{completadas} de {totalVisitas} ({porcentaje}%)</span>
            </div>
            <div className="progreso-container" style={{ margin: '6px 0 0' }}>
              <div className="progreso-barra" style={{ width: `${porcentaje}%` }}></div>
            </div>
          </div>

          {urlRutaDia && (
            <a
              href={urlRutaDia}
              target="_blank"
              rel="noreferrer"
              className="fantasma"
              style={{
                display: 'block',
                textAlign: 'center',
                marginBottom: 12,
                padding: '8px 10px',
                fontSize: 12.5,
                fontWeight: 600,
              }}
            >
              🗺️ Ver ruta del día ({ordenes.filter((o) => o.estadoVisita === 'PENDIENTE').length} controles)
            </a>
          )}

          {/* Buscador de visitas */}
          <div className="campo" style={{ marginBottom: 12 }}>
            <input
              type="text"
              placeholder="🔍 Buscar paciente o empresa..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </div>
        </>
      )}

      {ordenesFiltradas.length === 0 && ordenes !== null && (
        <p className="muted" style={{ textAlign: 'center', padding: '20px 0' }}>No se encontraron visitas.</p>
      )}

      {ordenesFiltradas.map((o) => {
        const ins = INSIGNIA[o.estadoVisita] ?? INSIGNIA.PENDIENTE;
        const pendiente = o.estadoVisita === 'PENDIENTE';
        return (
          <div key={o.ordenId} className={`tarjeta ${pendiente ? 'activa' : ''}`} style={{ transition: 'all 0.2s', borderLeft: pendiente ? '3px solid var(--verde)' : '0.5px solid var(--borde)' }}>
            <div className="fila">
              <div>
                <p className="muted" style={{ margin: 0, fontWeight: 500 }}>
                  ⏰ {new Date(o.programadaPara).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })} · {o.empresa.nombre}
                </p>
                <p style={{ margin: '4px 0 0', fontWeight: 600, fontSize: 14.5 }}>{o.trabajador.nombreCompleto}</p>
                {pendiente && (
                  <p className="muted" style={{ margin: '4px 0 0', fontSize: 11.5 }}>
                    📍 {o.domicilio}{' '}
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(o.domicilio)}`}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ fontWeight: 600 }}
                    >
                      Ver en el mapa
                    </a>
                  </p>
                )}
                {o.constanciaCodigo && <p className="mini" style={{ margin: '4px 0 0', fontFamily: 'monospace' }}>{o.constanciaCodigo}</p>}
              </div>
              <span className={`insignia ${ins.clase}`}>{ins.txt}</span>
            </div>
            {pendiente && (
              <div className="row-acciones">
                <button className="primario" style={{ flex: 1, padding: '8px 10px', fontSize: 13 }} onClick={() => iniciar(o)}>
                  Iniciar control
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
